import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import { MdCameraAlt, MdDelete, MdWarning } from "react-icons/md";
import { FiUpload, FiAlertTriangle } from "react-icons/fi";

const ImagePreviewModal = ({
  images,
  imageQualities = [], // New prop for blur analysis results
  onClose,
  onRetake,
  onDelete,
  onUpload,
  isUploading,
}) => {
  const blurryImagesCount = imageQualities.filter((q) => q?.isBlurry).length;

  const getQualityIndicator = (quality) => {
    if (!quality) return null;

    const indicators = {
      poor: { color: "#ff4444", icon: "🔴", text: "Poor Quality" },
      fair: { color: "#ff8800", icon: "🟡", text: "Fair Quality" },
      good: { color: "#44aa44", icon: "🟢", text: "Good Quality" },
      unknown: { color: "#888888", icon: "⚪", text: "Unknown" },
    };

    const indicator = indicators[quality.quality] || indicators.unknown;

    return (
      <div
        className="quality-indicator"
        style={{
          background: `${indicator.color}15`,
          color: indicator.color,
          border: `1px solid ${indicator.color}30`,
        }}
        title={quality.message}
      >
        <span className="quality-icon">{indicator.icon}</span>
        <span className="quality-text">{indicator.text}</span>
        {quality.isBlurry && <MdWarning className="warning-icon" />}
      </div>
    );
  };

  return (
    <div className="imgprv-modal-overlay">
      <div className="imgprv-modal-content">
        <button className="imgprv-modal-close" onClick={onClose}>
          <AiOutlineClose />
        </button>

        {/* Blur Warning Banner */}
        {blurryImagesCount > 0 && (
          <div className="blur-warning-banner">
            <FiAlertTriangle className="warning-icon" />
            <div className="warning-content">
              <h4>Image Quality Warning</h4>
              <p>
                {blurryImagesCount} of {images.length} images appear to be
                blurry or of poor quality. This may affect grading accuracy.
                Consider retaking these images for better results.
              </p>
            </div>
          </div>
        )}

        <div className="imgprv-modal-images">
          {images.map((img, idx) => (
            <div key={idx} className="imgprv-modal-image-container">
              <div className="image-wrapper">
                <img
                  src={img}
                  alt={`Scan ${idx + 1}`}
                  className="imgprv-modal-image"
                />

                {/* Quality indicator overlay */}
                {getQualityIndicator(imageQualities[idx])}

                {/* Blur warning overlay */}
                {imageQualities[idx]?.isBlurry && (
                  <div className="blur-overlay">
                    <MdWarning />
                    <span>Blurry Image</span>
                  </div>
                )}
              </div>

              <div className="imgprv-image-actions">
                <button onClick={() => onRetake(idx)} className="retake-btn">
                  <MdCameraAlt /> Retake
                </button>
                <button onClick={() => onDelete(idx)} className="delete-btn">
                  <MdDelete /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="imgprv-modal-footer">
          <button
            className="imgprv-upload-btn"
            onClick={() => onUpload?.()}
            disabled={images.length === 0 || isUploading}
          >
            {isUploading ? (
              <>
                <div className="loader" />
                Uploading...
              </>
            ) : (
              <>
                <FiUpload /> Upload Images
                {blurryImagesCount > 0 && (
                  <span className="warning-badge">{blurryImagesCount}</span>
                )}
              </>
            )}
          </button>
        </div>
      </div>

      <style jsx="true">{`
        .blur-warning-banner {
          background: linear-gradient(135deg, #fff3cd, #ffeaa7);
          border: 1px solid #ffc107;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .blur-warning-banner .warning-icon {
          color: #856404;
          font-size: 24px;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .warning-content h4 {
          margin: 0 0 8px 0;
          color: #856404;
          font-size: 16px;
          font-weight: 600;
        }

        .warning-content p {
          margin: 0;
          color: #856404;
          font-size: 14px;
          line-height: 1.4;
        }

        .image-wrapper {
          position: relative;
        }

        .quality-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          backdrop-filter: blur(4px);
          z-index: 2;
        }

        .quality-icon {
          font-size: 10px;
        }

        .quality-text {
          font-size: 10px;
        }

        .blur-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 68, 68, 0.9);
          color: white;
          padding: 8px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          backdrop-filter: blur(4px);
          border: 2px solid rgba(255, 255, 255, 0.3);
          z-index: 3;
        }

        .image-quality-details {
          margin-top: 8px;
          padding: 8px;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
          text-align: center;
        }

        .image-quality-details small {
          display: block;
          color: #666;
          font-size: 11px;
          margin-bottom: 2px;
        }

        .blur-score {
          color: #888 !important;
          font-family: monospace;
        }

        .warning-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #ff4444;
          color: white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        .imgprv-upload-btn {
          position: relative;
        }
      `}</style>
    </div>
  );
};

export default ImagePreviewModal;